import React from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LogOut, Upload, UserPlus, FileText, Plus, Shield, Trash2, Mail, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const loginWithGoogle = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AdminPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [user, setUser] = React.useState(location.state?.user || null);
  const [authState, setAuthState] = React.useState(location.state?.user ? "in" : "checking");
  const [tab, setTab] = React.useState("dashboard");

  React.useEffect(() => {
    if (location.state?.user) return;
    if (window.location.hash?.includes("session_id=")) return;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => { setUser(r.data); setAuthState("in"); })
      .catch(() => setAuthState("out"));
  }, []); // eslint-disable-line

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null); setAuthState("out");
  };

  if (authState === "checking") return <div className="p-16 text-center text-slate-400 font-mono-diplo text-xs">{t("common.loading")}</div>;

  if (authState === "out") {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <div className="tag-mono mb-2">{t("nav.admin")}</div>
        <h1 className="font-serif-diplo text-3xl font-bold text-[color:var(--navy)]">{t("admin.title")}</h1>
        <div className="gold-underline mx-auto mt-4 mb-8" />
        <p className="text-sm text-slate-600 mb-6">
          Only allowlisted organizers can sign in.
        </p>
        <button data-testid="admin-google-signin" onClick={loginWithGoogle}
          className="btn-navy w-full py-3 rounded-lg text-sm font-semibold">
          {t("admin.login")}
        </button>
        {new URLSearchParams(window.location.search).get("err") && (
          <div className="mt-4 text-xs text-rose-600" data-testid="admin-login-err">Sign-in failed. Ensure your email is allowlisted.</div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="tag-mono">{t("nav.admin")}</div>
          <h1 className="font-serif-diplo text-3xl font-bold text-[color:var(--navy)]">{t("admin.title")}</h1>
          <div className="text-xs text-slate-500 mt-1">{t("admin.loggedInAs")} <span className="font-mono-diplo">{user.email}</span></div>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="text-xs inline-flex items-center gap-1.5 text-slate-600 hover:text-rose-700">
          <LogOut className="w-3.5 h-3.5" /> {t("admin.logout")}
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200/70 mb-6">
        {["dashboard", "sessions", "walkin", "media", "reports", "admins"].map((tk) => (
          <button
            key={tk}
            data-testid={`admin-tab-${tk}`}
            data-active={tab === tk}
            className="day-tab"
            onClick={() => setTab(tk)}
          >
            {t(`admin.${tk}`)}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <Dashboard />}
      {tab === "sessions" && <SessionsManager />}
      {tab === "walkin" && <WalkinTab />}
      {tab === "media" && <MediaManager />}
      {tab === "reports" && <ReportsTab />}
      {tab === "admins" && <AdminsTab currentEmail={user.email} />}
    </div>
  );
}

function Dashboard() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const [data, setData] = React.useState(null);
  React.useEffect(() => { axios.get(`${API}/admin/dashboard`).then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-400 text-xs font-mono-diplo">{t("common.loading")}</div>;
  const cards = [
    { k: "bookings", v: data.totals.bookings, tone: "text-emerald-700 bg-emerald-500/10" },
    { k: "waitlist", v: data.totals.waitlist, tone: "text-amber-700 bg-amber-500/10" },
    { k: "checkins", v: data.totals.checkins, tone: "text-blue-700 bg-blue-500/10" },
    { k: "no_show", v: data.totals.no_show, tone: "text-rose-700 bg-rose-500/10" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.k} className={`rounded-xl p-4 ${c.tone}`} data-testid={`dash-card-${c.k}`}>
            <div className="tag-mono opacity-70">{t(`admin.totals.${c.k}`)}</div>
            <div className="font-serif-diplo text-3xl font-bold mt-1">{c.v}</div>
          </div>
        ))}
      </div>
      <div className="diplomatic-card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--navy)]/5 text-[color:var(--navy)]">
            <tr>
              <th className="text-start px-4 py-3 tag-mono">Session</th>
              <th className="text-start px-4 py-3 tag-mono">{t("day")}</th>
              <th className="text-start px-4 py-3 tag-mono">{t("admin.reserved")}/{t("admin.capacity")}</th>
              <th className="text-start px-4 py-3 tag-mono">Wait</th>
              <th className="text-start px-4 py-3 tag-mono">Check-in</th>
              <th className="text-start px-4 py-3 tag-mono">No-show</th>
            </tr>
          </thead>
          <tbody>
            {data.sessions.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-4 py-3">{s.title?.[lng] || s.title?.en}</td>
                <td className="px-4 py-3">{s.day}</td>
                <td className="px-4 py-3 font-mono-diplo text-xs">{s.reserved_count}/{s.capacity}</td>
                <td className="px-4 py-3 font-mono-diplo text-xs">{s.waitlist_count}</td>
                <td className="px-4 py-3 font-mono-diplo text-xs">{s.checkin_count}</td>
                <td className="px-4 py-3 font-mono-diplo text-xs">{s.no_show > 0 ? <span className="text-rose-700">{s.no_show}</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SessionsManager() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const [sessions, setSessions] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [descEdit, setDescEdit] = React.useState(null);
  const [importResult, setImportResult] = React.useState(null);
  const [importing, setImporting] = React.useState(false);
  const [replaceAll, setReplaceAll] = React.useState(false);
  const [autoTranslate, setAutoTranslate] = React.useState(true);
  const [previewFile, setPreviewFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [titleEdits, setTitleEdits] = React.useState({});
  const load = () => axios.get(`${API}/sessions`).then((r) => setSessions(r.data));
  React.useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete session and all bookings?")) return;
    await axios.delete(`${API}/admin/sessions/${id}`);
    toast.success("Deleted"); load();
  };
  const runPreview = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setPreview(null); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/admin/sessions/import?replace_all=${replaceAll}&auto_translate=${autoTranslate}&dry_run=true`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(res.data);
      setPreviewFile(file);
      // seed title edits from preview so admins can tweak translations inline
      const seeded = {};
      for (const r of res.data.preview_rows || []) {
        seeded[r.line] = { fr: r.title.fr || "", pt: r.title.pt || "", ar: r.title.ar || "" };
      }
      setTitleEdits(seeded);
      const tx = res.data.translations_applied || 0;
      toast.success(`Preview ready · ${res.data.would_create} valid · ${res.data.errors} errors${tx ? ` · ${tx} translations` : ""}`);
    } catch (er) {
      toast.error(er.response?.data?.detail || "Preview failed");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };
  const confirmImport = async () => {
    if (!previewFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", previewFile);
      // Only include overrides that actually differ from the preview values
      const diffed = {};
      for (const r of preview?.preview_rows || []) {
        const e = titleEdits[r.line] || {};
        const patch = {};
        for (const loc of ["fr", "pt", "ar"]) {
          const cur = (e[loc] ?? "").trim();
          const original = (r.title[loc] ?? "").trim();
          if (cur && cur !== original) patch[loc] = cur;
        }
        if (Object.keys(patch).length) diffed[r.line] = patch;
      }
      if (Object.keys(diffed).length) fd.append("overrides", JSON.stringify(diffed));
      const res = await axios.post(
        `${API}/admin/sessions/import?replace_all=${replaceAll}&auto_translate=${autoTranslate}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportResult(res.data);
      setPreview(null); setPreviewFile(null); setTitleEdits({});
      const tx = res.data.translations_applied || 0;
      const ov = res.data.overrides_applied || 0;
      toast.success(`${res.data.created} created · ${res.data.errors} errors${tx ? ` · ${tx} translations` : ""}${ov ? ` · ${ov} edits` : ""}`);
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };
  const cancelPreview = () => { setPreview(null); setPreviewFile(null); setTitleEdits({}); };
  const setEdit = (line, loc, value) => setTitleEdits((prev) => ({ ...prev, [line]: { ...(prev[line] || {}), [loc]: value } }));
  return (
    <div className="space-y-4">
      {/* Bulk import panel */}
      <div className="diplomatic-card p-4 space-y-3" data-testid="bulk-import-panel">
        <div className="flex items-center gap-2">
          <span className="tag-mono">Bulk import</span>
          <span className="text-[10px] text-[color:var(--ink-3)]">CSV or XLSX · one session per row</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a data-testid="import-template-xlsx"
             href={`${API}/admin/sessions/import/template?fmt=xlsx`}
             className="text-xs border border-[color:var(--line-strong)] px-3 py-2 rounded-lg font-semibold hover:border-[color:var(--forest)] hover:text-[color:var(--forest)]">
            Download XLSX template
          </a>
          <a data-testid="import-template-csv"
             href={`${API}/admin/sessions/import/template?fmt=csv`}
             className="text-xs border border-[color:var(--line-strong)] px-3 py-2 rounded-lg font-semibold hover:border-[color:var(--forest)] hover:text-[color:var(--forest)]">
            Download CSV template
          </a>
          <label className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-2)] ms-2">
            <input data-testid="import-replace-all" type="checkbox" checked={replaceAll} onChange={(e) => setReplaceAll(e.target.checked)} className="w-3.5 h-3.5" />
            Replace ALL existing sessions
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-[color:var(--ink-2)]" title="If FR / PT / AR title columns are blank, Claude will translate the English title for you.">
            <input data-testid="import-auto-translate" type="checkbox" checked={autoTranslate} onChange={(e) => setAutoTranslate(e.target.checked)} className="w-3.5 h-3.5" />
            Auto-translate missing FR / PT / AR titles
          </label>
          <label className={`ms-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer ${preview ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "btn-gold"}`} data-testid="import-upload-label">
            {importing ? "Parsing…" : preview ? "Preview loaded ↓" : "Choose file & preview"}
            <input data-testid="import-upload-input" type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={runPreview} disabled={importing || !!preview} />
          </label>
        </div>

        {preview && (
          <div className="rounded-xl border border-[color:var(--line-strong)] bg-white overflow-hidden" data-testid="import-preview-panel">
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[color:var(--forest-soft)] border-b border-[color:var(--line)]">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="tag-mono text-[color:var(--forest-dark)]">Preview</span>
                <span className="text-[color:var(--ink-2)]">
                  <b>{preview.would_create}</b> ready · <b className={preview.errors ? "text-rose-700" : ""}>{preview.errors}</b> errors
                  {preview.translations_applied ? <> · <b>{preview.translations_applied}</b> title translations</> : null}
                  {preview.description_translations_applied ? <> · <b>{preview.description_translations_applied}</b> description translations</> : null}
                  {preview.would_replace_all ? <> · <span className="text-rose-700 font-semibold">will REPLACE existing sessions</span></> : null}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="import-cancel-preview" onClick={cancelPreview} className="text-xs px-3 py-1.5 rounded-lg border border-[color:var(--line-strong)] font-semibold hover:bg-slate-50">
                  Cancel
                </button>
                <button data-testid="import-confirm" onClick={confirmImport} disabled={importing || preview.would_create === 0}
                        className="text-xs px-3 py-1.5 rounded-lg bg-[color:var(--forest)] text-white font-semibold hover:bg-[color:var(--forest-dark)] disabled:opacity-50">
                  {importing ? "Importing…" : `Confirm & import ${preview.would_create}`}
                </button>
              </div>
            </div>

            {preview.error_rows?.length > 0 && (
              <div className="px-4 py-2 bg-rose-50 border-b border-rose-200 text-xs" data-testid="preview-errors">
                <div className="font-semibold text-rose-800 mb-1">{preview.error_rows.length} row(s) will be skipped:</div>
                <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                  {preview.error_rows.slice(0, 40).map((er) => (
                    <li key={`err-${er.line}`} className="text-rose-700"><span className="font-mono">Line {er.line}</span> — {er.title || "(no title)"} — <em>{er.error}</em></li>
                  ))}
                </ul>
              </div>
            )}

            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs" data-testid="preview-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)]">
                    <th className="px-3 py-2">#</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Title (EN)</th>
                    <th className="px-3 py-2">FR</th>
                    <th className="px-3 py-2">PT</th>
                    <th className="px-3 py-2" dir="rtl">AR</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2">Date · Time</th>
                    <th className="px-3 py-2">Venue</th>
                    <th className="px-3 py-2">Organization</th>
                    <th className="px-3 py-2">Topic</th>
                    <th className="px-3 py-2">Cap</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview_rows.map((r) => {
                    const s = new Date(r.starts_at); const e = new Date(r.ends_at);
                    const fmt = (d) => d.toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit", hour12: false });
                    const edit = titleEdits[r.line] || {};
                    const cellInputCls = "w-full bg-transparent px-1 py-0.5 rounded border border-transparent hover:border-[color:var(--line-strong)] focus:border-[color:var(--forest)] focus:bg-white focus:outline-none text-[color:var(--ink-2)]";
                    return (
                      <tr key={r.line} className="border-t border-slate-100 align-top" data-testid={`preview-row-${r.line}`}>
                        <td className="px-3 py-2 font-mono text-[color:var(--ink-3)]">{r.line}</td>
                        <td className="px-3 py-2"><span className="tag-mono">{r.type}</span>{r.closed ? <div className="text-[9px] text-rose-700 font-mono mt-0.5">CLOSED</div> : null}{r.time_tbc ? <div className="text-[9px] text-slate-600 font-mono mt-0.5">TBC</div> : null}</td>
                        <td className="px-3 py-2 font-semibold text-[color:var(--ink)] max-w-[220px]">{r.title.en}</td>
                        <td className="px-2 py-1 max-w-[200px]">
                          <input data-testid={`edit-fr-${r.line}`} value={edit.fr ?? r.title.fr} onChange={(ev) => setEdit(r.line, "fr", ev.target.value)} className={cellInputCls} />
                        </td>
                        <td className="px-2 py-1 max-w-[200px]">
                          <input data-testid={`edit-pt-${r.line}`} value={edit.pt ?? r.title.pt} onChange={(ev) => setEdit(r.line, "pt", ev.target.value)} className={cellInputCls} />
                        </td>
                        <td className="px-2 py-1 max-w-[200px]" dir="rtl">
                          <input data-testid={`edit-ar-${r.line}`} value={edit.ar ?? r.title.ar} onChange={(ev) => setEdit(r.line, "ar", ev.target.value)} className={cellInputCls} dir="rtl" />
                        </td>
                        <td className="px-3 py-2 max-w-[280px] text-[color:var(--ink-2)]" data-testid={`preview-desc-${r.line}`}>
                          {r.description?.en ? (
                            <div className="space-y-1">
                              <div className="line-clamp-2 leading-snug">{r.description.en}</div>
                              <div className="flex gap-1.5 text-[9px] font-mono uppercase tracking-widest">
                                {["fr", "pt", "ar"].map((loc) => {
                                  const filled = !!(r.description?.[loc] || "").trim() && (r.description[loc] !== r.description.en);
                                  return (
                                    <span key={loc} className={filled ? "text-[color:var(--forest-dark)] font-semibold" : "text-[color:var(--ink-3)]"}>
                                      {filled ? "✓" : "·"} {loc}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <span className="text-[color:var(--ink-3)] italic">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{s.toLocaleDateString("en", { month: "short", day: "2-digit" })} · {fmt(s)}–{fmt(e)}</td>
                        <td className="px-3 py-2 max-w-[160px]">{r.room}</td>
                        <td className="px-3 py-2 max-w-[180px] text-[color:var(--forest-dark)] font-semibold">{r.organization || <span className="text-[color:var(--ink-3)] italic font-normal">—</span>}</td>
                        <td className="px-3 py-2 max-w-[160px] italic text-[color:var(--ink-2)]">{r.topic || <span className="text-[color:var(--ink-3)] font-normal not-italic">—</span>}</td>
                        <td className="px-3 py-2 font-mono">{r.capacity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {preview.preview_rows.length === 0 && (
                <div className="p-6 text-center text-xs text-[color:var(--ink-3)]">No valid rows to import. Fix the errors above and try again.</div>
              )}
            </div>
          </div>
        )}

        {importResult && (
          <div className="text-xs bg-[color:var(--forest-soft)] rounded-lg p-3 space-y-1" data-testid="import-result">
            <div className="font-semibold text-[color:var(--forest-dark)]">
              ✔ {importResult.created} sessions created · {importResult.errors} errors {importResult.replaced_all ? "· replaced everything" : ""}{importResult.translations_applied ? ` · ${importResult.translations_applied} auto-translations` : ""}
            </div>
            {importResult.error_rows?.length > 0 && (
              <details className="mt-1">
                <summary className="cursor-pointer text-rose-700 font-semibold">See errors ({importResult.error_rows.length})</summary>
                <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                  {importResult.error_rows.slice(0, 20).map((er) => (
                    <li key={`ires-${er.line}`} className="text-rose-700"><span className="font-mono">Line {er.line}</span> — {er.title || "(no title)"} — <em>{er.error}</em></li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button data-testid="admin-add-session-btn" onClick={() => setShowForm(true)} className="btn-gold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> {t("admin.addSession")}
        </button>
      </div>
      {showForm && <SessionForm onClose={() => { setShowForm(false); load(); }} />}
      <div className="grid md:grid-cols-2 gap-3">
        {sessions.map((s) => (
          <div key={s.id} className="diplomatic-card p-4" data-testid={`admin-session-${s.id}`}>
            <div className="tag-mono">{t(`filters.${s.type}`)} · {new Date(s.starts_at).toLocaleDateString(lng, {month: "short", day: "2-digit"})}</div>
            <div className="font-serif-diplo text-base text-[color:var(--navy)]">{s.title?.[lng] || s.title?.en}</div>
            {s.organization && <div className="text-xs text-[color:var(--forest)] mt-0.5 font-semibold truncate">{s.organization}</div>}
            {s.topic && <div className="text-[11px] text-[color:var(--ink-2)] mt-0.5 italic truncate">Topic · {s.topic}</div>}
            <div className="text-xs text-slate-500 mt-1">{s.room} · {s.reserved_count}/{s.capacity}{s.closed ? " · closed" : ""}{s.time_tbc ? " · TBC" : ""}</div>
            <div className="mt-3 flex gap-2">
              <button data-testid={`admin-edit-description-${s.id}`} onClick={() => setDescEdit(s)}
                className="text-xs border border-[color:var(--forest)] text-[color:var(--forest-dark)] px-3 py-1.5 rounded-lg font-semibold hover:bg-[color:var(--forest-soft)]">
                {s.description?.en ? "Edit description" : "+ Add description"}
              </button>
              <button data-testid={`admin-delete-session-${s.id}`} onClick={() => del(s.id)}
                className="text-xs border border-rose-200 text-rose-700 px-3 py-1.5 rounded-lg">Delete</button>
            </div>
          </div>
        ))}
      </div>
      {descEdit && <DescriptionEditor session={descEdit} onClose={(dirty) => { setDescEdit(null); if (dirty) load(); }} />}
    </div>
  );
}

function DescriptionEditor({ session, onClose }) {
  const [text, setText] = React.useState(session.description?.en || "");
  const [saving, setSaving] = React.useState(false);
  const save = async () => {
    setSaving(true);
    try {
      // Send only the English copy; backend auto-translates to FR/PT/AR via Claude.
      await axios.put(`${API}/admin/sessions/${session.id}`, {
        description: { en: text, fr: "", pt: "", ar: "" },
      });
      toast.success("Description saved · auto-translated to FR / PT / AR");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => onClose(false)}>
      <div className="bg-white rounded-2xl p-6 max-w-xl w-full animate-fade-up" onClick={(e) => e.stopPropagation()} data-testid="desc-editor">
        <div className="tag-mono mb-1">About this session</div>
        <h3 className="font-serif-diplo text-lg text-[color:var(--ink)] mb-3">{session.title?.en}</h3>
        <textarea
          data-testid="desc-editor-textarea"
          rows={8}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="A short paragraph describing the objectives, speakers, outcomes…"
          className="w-full rounded-lg px-3 py-2.5 text-sm border border-[color:var(--line-strong)] focus:border-[color:var(--forest)] outline-none resize-y leading-relaxed"
        />
        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mt-2">
          English copy — Claude will auto-translate this to French, Portuguese and Arabic on save.
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="desc-editor-cancel" onClick={() => onClose(false)} className="text-xs px-4 py-2 rounded-lg border border-[color:var(--line-strong)] font-semibold">Cancel</button>
          <button data-testid="desc-editor-save" onClick={save} disabled={saving}
            className="text-xs px-4 py-2 rounded-lg bg-[color:var(--forest)] text-white font-semibold hover:bg-[color:var(--forest-dark)] disabled:opacity-50">
            {saving ? "Saving…" : "Save description"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionForm({ onClose }) {
  const [form, setForm] = React.useState({
    title_en: "", type: "workshop", day: 7, starts_at: "2026-10-07T09:00", ends_at: "2026-10-07T11:00",
    room: "", facilitator: "", capacity: 50,
    organization: "", topic: "",
    focal_person: "", focal_contact: "",
    focal_person_2: "", focal_contact_2: "",
    description_en: "",
  });
  const submit = async () => {
    try {
      await axios.post(`${API}/admin/sessions`, {
        title: { en: form.title_en, fr: form.title_en, pt: form.title_en, ar: form.title_en },
        // Only send the EN copy; backend auto-translates to FR/PT/AR on save.
        description: { en: form.description_en, fr: "", pt: "", ar: "" },
        type: form.type, day: Number(form.day),
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
        room: form.room, facilitator: form.facilitator, capacity: Number(form.capacity),
        organization: form.organization || null,
        topic: form.topic || null,
        focal_person: form.focal_person || null,
        focal_contact: form.focal_contact || null,
        focal_person_2: form.focal_person_2 || null,
        focal_contact_2: form.focal_contact_2 || null,
      });
      toast.success("Session added"); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "w-full rounded-lg px-3 py-2.5 text-sm";
  const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-[color:var(--forest)] mb-1 font-bold";
  return (
    <div className="diplomatic-card p-5 space-y-4" data-testid="session-form">
      <div>
        <label className={labelCls}>Title (EN)</label>
        <input data-testid="sf-title" placeholder="e.g. Understanding Democratic Governance" value={form.title_en} onChange={(e) => upd("title_en", e.target.value)} className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Type</label>
          <select data-testid="sf-type" value={form.type} onChange={(e) => upd("type", e.target.value)} className={inputCls}>
            <option value="workshop">Workshop</option><option value="training">Training</option><option value="side_event">Side Event</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Day</label>
          <select data-testid="sf-day" value={form.day} onChange={(e) => upd("day", e.target.value)} className={inputCls}>
            {[7, 8, 9, 10, 11, 12, 13, 14, 15].map((d) => {
              const dt = new Date(Date.UTC(2026, 9, d));
              const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getUTCDay()];
              return <option key={d} value={d}>Day · {dow} {d} Oct 2026</option>;
            })}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Starts at</label>
          <input data-testid="sf-start" type="datetime-local" value={form.starts_at} onChange={(e) => upd("starts_at", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Ends at</label>
          <input data-testid="sf-end" type="datetime-local" value={form.ends_at} onChange={(e) => upd("ends_at", e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Room</label>
          <input data-testid="sf-room" placeholder="Grand Ballroom" value={form.room} onChange={(e) => upd("room", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Facilitator</label>
          <input data-testid="sf-facilitator" placeholder="Speaker or team" value={form.facilitator} onChange={(e) => upd("facilitator", e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Organization / Organizer</label>
          <input data-testid="sf-organization" placeholder="e.g. ACDHRS · Maat for Peace" value={form.organization} onChange={(e) => upd("organization", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Topic of Event</label>
          <input data-testid="sf-topic" placeholder="e.g. Election observation methodology" value={form.topic} onChange={(e) => upd("topic", e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Focal person</label>
          <input data-testid="sf-focal-person" placeholder="Primary contact" value={form.focal_person} onChange={(e) => upd("focal_person", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Focal WhatsApp</label>
          <input data-testid="sf-focal-contact" placeholder="+221 …" value={form.focal_contact} onChange={(e) => upd("focal_contact", e.target.value)} className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Co-focal (optional)</label>
          <input data-testid="sf-focal-person-2" placeholder="Second contact person" value={form.focal_person_2} onChange={(e) => upd("focal_person_2", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Co-focal WhatsApp</label>
          <input data-testid="sf-focal-contact-2" placeholder="+221 …" value={form.focal_contact_2} onChange={(e) => upd("focal_contact_2", e.target.value)} className={inputCls} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Capacity</label>
        <input data-testid="sf-capacity" type="number" min="1" placeholder="50" value={form.capacity} onChange={(e) => upd("capacity", e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>About this session (shown on the detail page)</label>
        <textarea
          data-testid="sf-description"
          rows={5}
          placeholder="A short paragraph describing the objectives, speakers, outcomes…"
          value={form.description_en}
          onChange={(e) => upd("description_en", e.target.value)}
          className="w-full rounded-lg px-3 py-2.5 text-sm border border-[color:var(--line-strong)] focus:border-[color:var(--forest)] outline-none resize-y leading-relaxed"
        />
        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mt-1">
          Written in English — Claude will auto-translate this to FR / PT / AR when you save.
        </div>
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <button onClick={onClose} className="text-sm px-4 py-2 border border-[color:var(--line-strong)] rounded-lg font-semibold">Cancel</button>
        <button data-testid="sf-submit" onClick={submit} className="btn-navy text-sm px-5 py-2 rounded-lg font-semibold">Save</button>
      </div>
    </div>
  );
}

function AdminsTab({ currentEmail }) {
  const [list, setList] = React.useState(null);
  const [email, setEmail] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    axios.get(`${API}/admin/admins`).then((r) => setList(r.data)).catch(() => toast.error("Failed to load admins"));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/admins`, { email: em, note: note.trim() || null });
      toast.success(`${em} can now sign in as admin${res.data?.invite_email_sent ? " · email sent" : ""}`);
      setEmail(""); setNote(""); load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const revoke = async (em) => {
    if (!window.confirm(`Revoke admin access for ${em}?`)) return;
    try {
      await axios.delete(`${API}/admin/admins/${encodeURIComponent(em)}`);
      toast.success(`Revoked ${em}`);
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admins-tab">
      <div className="diplomatic-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-4 h-4 text-[color:var(--forest)]" />
          <h2 className="font-serif-diplo text-lg text-[color:var(--ink)]">Invite an admin</h2>
        </div>
        <p className="text-xs text-[color:var(--ink-2)] mb-4">
          Invited admins sign in with their Google account. They get the same rights as you — add, edit and delete sessions, upload media, invite more admins. Only invite people you trust.
        </p>
        <form onSubmit={invite} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-stretch">
          <div className="relative">
            <Mail className="w-4 h-4 text-[color:var(--ink-3)] absolute start-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-invite-email"
              type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="name@organisation.org"
              className="w-full ps-9 pe-3 py-2.5 rounded-lg text-sm border border-[color:var(--line-strong)] focus:border-[color:var(--forest)] outline-none"
            />
          </div>
          <input
            data-testid="admin-invite-note"
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Role / organisation (optional)"
            className="w-full px-3 py-2.5 rounded-lg text-sm border border-[color:var(--line-strong)] focus:border-[color:var(--forest)] outline-none"
          />
          <button data-testid="admin-invite-submit" type="submit" disabled={busy || !email}
                  className="btn-gold px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <UserPlus className="w-4 h-4" /> {busy ? "…" : "Invite"}
          </button>
        </form>
      </div>

      <div className="diplomatic-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--line)]">
          <div className="flex items-center gap-2">
            <span className="tag-mono">Current admins</span>
            {list && <span className="text-xs text-[color:var(--ink-3)]">{list.admins.length}</span>}
          </div>
        </div>
        {!list && <div className="p-6 text-xs text-[color:var(--ink-3)]">Loading…</div>}
        {list && (
          <ul className="divide-y divide-[color:var(--line)]" data-testid="admin-list">
            {list.admins.map((a) => {
              const isMe = a.email === currentEmail?.toLowerCase();
              return (
                <li key={a.email} className="px-5 py-3 flex items-center gap-3" data-testid={`admin-row-${a.email}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold ${a.source === "env" ? "bg-[color:var(--forest)]" : "bg-[color:var(--amber)]"}`}>
                    {a.email[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">{a.email}</span>
                      {isMe && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[color:var(--forest-soft)] text-[color:var(--forest-dark)]">You</span>}
                      {a.source === "env"
                        ? <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"><Lock className="w-2.5 h-2.5" /> Permanent (env)</span>
                        : <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[color:var(--amber-soft)] text-[color:var(--amber-dark)]">Invited</span>}
                    </div>
                    {a.note && <div className="text-xs text-[color:var(--ink-2)] italic mt-0.5 truncate">{a.note}</div>}
                    {a.added_by && (
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-3)] mt-0.5">
                        Invited by {a.added_by}{a.added_at ? ` · ${new Date(a.added_at).toLocaleDateString()}` : ""}
                      </div>
                    )}
                  </div>
                  <button
                    data-testid={`admin-revoke-${a.email}`}
                    disabled={!a.can_revoke || isMe}
                    onClick={() => revoke(a.email)}
                    title={isMe ? "You cannot revoke your own access" : (!a.can_revoke ? "Permanent admins can only be changed via env" : "Revoke access")}
                    className="text-xs inline-flex items-center gap-1 text-rose-700 hover:text-rose-900 disabled:text-slate-400 disabled:cursor-not-allowed disabled:hover:text-slate-400 font-semibold"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Revoke
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function WalkinTab() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "en";
  const [sessions, setSessions] = React.useState([]);
  const [form, setForm] = React.useState({ session_id: "", name: "", phone: "" });
  React.useEffect(() => { axios.get(`${API}/sessions`).then((r) => setSessions(r.data)); }, []);
  const submit = async () => {
    try {
      await axios.post(`${API}/admin/walk-in`, form);
      toast.success("Walk-in registered & checked in");
      setForm({ session_id: "", name: "", phone: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="max-w-lg space-y-3">
      <div className="tag-mono">{t("admin.walkinTitle")}</div>
      <select data-testid="walkin-session" value={form.session_id} onChange={(e) => setForm({ ...form, session_id: e.target.value })}
        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
        <option value="">— Choose session —</option>
        {sessions.map((s) => <option key={s.id} value={s.id}>{t("day")} {s.day} · {s.title?.[lng] || s.title?.en}</option>)}
      </select>
      <input data-testid="walkin-name" placeholder="Name / Delegation" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
      <input data-testid="walkin-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
      <button data-testid="walkin-submit" onClick={submit} disabled={!form.session_id || !form.name}
        className="btn-navy w-full py-2.5 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
        <UserPlus className="w-4 h-4" /> Register & Check in
      </button>
    </div>
  );
}

function MediaManager() {
  const [items, setItems] = React.useState([]);
  const [uploading, setUploading] = React.useState(false);
  const [category, setCategory] = React.useState("photo");
  const [filter, setFilter] = React.useState("all");
  const load = () => axios.get(`${API}/media`).then((r) => setItems(r.data));
  React.useEffect(() => { load(); }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/media?category=${category}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded to ${category}`); load();
    } catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const del = async (id) => {
    await axios.delete(`${API}/admin/media/${id}`);
    toast.success("Removed"); load();
  };
  const shown = filter === "all" ? items : items.filter((m) => m.category === filter);
  const catBadge = (c) => ({
    photo: "bg-emerald-500/15 text-emerald-700",
    agenda: "bg-amber-500/15 text-amber-700",
    document: "bg-blue-500/15 text-blue-700",
  }[c] || "bg-slate-200 text-slate-700");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="tag-mono">Upload as</span>
        {["photo", "agenda", "document"].map((c) => (
          <button
            key={c}
            data-testid={`media-cat-${c}`}
            data-active={category === c}
            onClick={() => setCategory(c)}
            className="filter-pill filter-pill-sm capitalize"
          >
            {c}
          </button>
        ))}
        <label className="btn-gold inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer ms-auto" data-testid="media-upload-label">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload file"}
          <input data-testid="media-upload-input" type="file" className="hidden" onChange={upload} />
        </label>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {["all", "photo", "agenda", "document"].map((c) => (
          <button
            key={c}
            data-testid={`media-filter-${c}`}
            data-active={filter === c}
            onClick={() => setFilter(c)}
            className="filter-pill filter-pill-sm capitalize"
          >
            {c} ({c === "all" ? items.length : items.filter((m) => m.category === c).length})
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {shown.map((m) => (
          <div key={m.id} className="diplomatic-card overflow-hidden" data-testid={`admin-media-item-${m.id}`}>
            {(m.content_type || "").startsWith("image/") ? (
              <img src={`${API}/media/${m.id}/file`} alt="" className="w-full h-40 object-cover" />
            ) : (
              <div className="w-full h-40 bg-[color:var(--forest-soft)] flex items-center justify-center">
                <FileText className="w-8 h-8 text-[color:var(--forest)]" />
              </div>
            )}
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${catBadge(m.category)}`}>{m.category || "photo"}</span>
                <span className="text-[10px] text-[color:var(--ink-3)]">{(m.size / 1024).toFixed(0)} KB</span>
              </div>
              <div className="text-xs text-[color:var(--ink)] truncate">{m.original_filename}</div>
              <div className="flex justify-between">
                <a href={`${API}/media/${m.id}/file`} target="_blank" rel="noreferrer" className="text-xs text-[color:var(--forest)] font-semibold">Open</a>
                <button data-testid={`admin-media-del-${m.id}`} onClick={() => del(m.id)} className="text-xs text-rose-700 font-semibold">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsTab() {
  const { t } = useTranslation();
  const download = () => {
    window.open(`${API}/admin/reports/csv`, "_blank");
  };
  return (
    <div className="max-w-lg">
      <p className="text-sm text-slate-600 mb-4">Export all registrations, attendance and no-show data.</p>
      <button data-testid="reports-csv-btn" onClick={download} className="btn-navy px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2">
        <FileText className="w-4 h-4" /> {t("admin.exportCsv")}
      </button>
    </div>
  );
}
